import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
    DndContext,
    DragOverlay,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
    pointerWithin,
} from '@dnd-kit/core';
import {
    useMediaQuery,
    useTheme,
    CircularProgress,
    Box,
    Typography,
    Portal,
} from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { useScrollSync } from 'hooks/useScrollSync';
import api from 'state/axios';
import FlexCenter from 'components/FlexCenter';
import ConfirmDialog from 'components/ConfirmDialog';
import GanttLayout from './GanttLayout';
import TopLeftPanel from './TopLeftPanel';
import TopRightPanel from './TopRightPanel';
import BottomLeftPanel from './BottomLeftPanel';
import BottomRightPanel from './BottomRightPanel';
import GanttBar from "./GanttBar";
import AssignmentDialog from "./AssignmentDialog";
import AutoAssignmentDialog from "./AutoAssignmentDialog";
import EmployeeScheduleDialog from "./EmployeeScheduleDialog";
import { exportScheduleToExcel } from "./helpers";


const GanttRoot = ({
    rowHeight = 40,
    cellWidth = 6,
    autoAssignmentOpen = false,
    onAutoAssignmentClose,
    refreshRef,
    exportRef,
    employeeFilter,
    onEmployeesLoaded,
    filteredEmployeeIds,
    canCreate = false,
    canEdit = false,
    canDelete = false,
    canPublish = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
        
    const { startDate, endDate } = useDateRangeContext();
  
    const [loading, setLoading] = useState(true);   // full-screen spinner chỉ lần đầu
    const [isFetching, setIsFetching] = useState(false);  // indicator nhỏ khi re-fetch
    const [error, setError] = useState(null);
    const hasLoadedRef = useRef(false);

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 2 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
    );
    const { syncX, syncY } = useScrollSync();
    const timelineScrollRef = useRef(null);
    const topLeftScrollRef = useRef(null);
    const topRightScrollRef = useRef(null);
    const bottomLeftScrollRef = useRef(null);
    const bottomRightScrollRef = useRef(null);
    
    const leftSize = isMobile ? 40 : 20;
    const rightSize = 100 - leftSize;

    const [employees, setEmployees] = useState([]);
    const employeeMap = useMemo(() => new Map(employees.map(e => [e.id, e])), [employees]);
    const [unassignments, setUnassignments] = useState([]);
    const [activeBar, setActiveBar] = useState(null);
    const [selectedBarId, setSelectedBarId] = useState(null);
    const [editingBar, setEditingBar] = useState(null);
    const [scheduleEmployee, setScheduleEmployee] = useState(null);
    const [laborOverride, setLaborOverride] = useState(null); // { type, assignmentId, targetEmployeeId, fromEmployeeId?, rawAssignment, willBeFull?, violations }

    
    /* ---------- LOAD SHIFT ---------- */    
    const fetchAssignments = useCallback(async () => {
        if (!startDate || !endDate) return [];
        const payload = { startDate, endDate };

        return await api.post('/assignments', 
            payload
        );
    }, [startDate, endDate]);   

    /* ---------- LOAD EMPLOYEE ---------- */
    const fetchEmployees = useCallback(async () => {
        return await api.get('/employees/active');
    }, []);
     
    /* ---------- LOAD DATA ---------- */
    const loadData = useCallback(async () => {
        if (hasLoadedRef.current) {
            setIsFetching(true);  // Đã có data → chỉ hiện indicator nhỏ, giữ gantt visible
        } else {
            setLoading(true);     // Lần đầu → full-screen spinner
        }
        setError(null);

        try {
            const [assignmentsRes, employeesRes] = await Promise.all([
                fetchAssignments(),
                fetchEmployees(),
            ]);

            /* ---------- ASSIGNMENTS ---------- */
            const allAssignments = assignmentsRes?.data || [];

            const unassigned = [];
            const assignmentByEmployeeId = new Map();

            for (const assignment of allAssignments) {
                const assignees = assignment.assignees || [];
                const requiredCount = assignment.requiredEmployees ?? assignment.shiftId?.requiredEmployees ?? 1;

                // Pool: tất cả shift chưa đủ nhân viên
                if (assignees.length < requiredCount) {
                    unassigned.push(assignment);
                }

                // Employee map: tất cả shift đã assign cho nhân viên nào đó
                for (const ae of assignees) {
                    const key = String(ae.employeeId);
                    if (!assignmentByEmployeeId.has(key)) {
                        assignmentByEmployeeId.set(key, []);
                    }
                    assignmentByEmployeeId.get(key).push(assignment);
                }
            }

            /* ---------- EMPLOYEES ---------- */
            const employees = (employeesRes?.data || [])
                .map((item) => {
                    const id = String(item._id);

                    return {
                        id,
                        employeeCode: item.employeeCode,
                        label: `${item.employeeCode} - ${item.employeeName} (${item.rank} | ${item.location})`,
                        employee: item,
                        assignments: assignmentByEmployeeId.get(id) || [],
                    };
                })

            setUnassignments(unassigned);
            setEmployees(employees);
            onEmployeesLoaded?.(employeesRes?.data || []);
            hasLoadedRef.current = true;
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to fetch data!");
            setError(err);
        } finally {
            setLoading(false);
            setIsFetching(false);
        }
    }, [fetchAssignments, fetchEmployees, onEmployeesLoaded]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (refreshRef) refreshRef.current = loadData;
    }, [loadData, refreshRef]);

    /* ---------- BULK-CREATE OPTIMISTIC HELPERS (dùng bởi BottomLeftPanel) ---------- */
    const addUnassignmentsOptimistic = useCallback((items) => {
        setUnassignments(prev => [...items, ...prev]);
    }, []);

    const replaceOptimisticAssignments = useCallback((tempPrefix, realAssignments) => {
        setUnassignments(prev => {
            const map = new Map();
            prev.forEach(a => {
                if (a.__optimistic && String(a._id).startsWith(tempPrefix)) return;
                map.set(String(a._id), a);
            });
            realAssignments.forEach(a => map.set(String(a._id), a));
            return Array.from(map.values());
        });
    }, []);

    const rollbackOptimisticAssignments = useCallback((tempPrefix) => {
        setUnassignments(prev =>
            prev.filter(a => !String(a._id).startsWith(tempPrefix))
        );
    }, []);

    /* ---------- DRAG OPTIMISTIC HELPERS ---------- */
    const removeFromUnassigned = useCallback((assignmentId) => {
        setUnassignments(prev => prev.filter(a => String(a._id) !== String(assignmentId)));
    }, []);

    const addToEmployee = useCallback((employeeId, assignment) => {
        setEmployees(prev => prev.map(emp =>
            emp.id === employeeId
                ? { ...emp, assignments: [...emp.assignments, assignment] }
                : emp
        ));
    }, []);

    // replaceId: _id of optimistic entry to swap out
    const replaceInEmployee = useCallback((employeeId, replaceId, newAssignment) => {
        setEmployees(prev => prev.map(emp => {
            if (emp.id !== employeeId) return emp;
            return {
                ...emp,
                assignments: emp.assignments.map(a =>
                    String(a._id) === String(replaceId) ? newAssignment : a
                ),
            };
        }));
    }, []);

    const removeFromEmployee = useCallback((employeeId, assignmentId) => {
        setEmployees(prev => prev.map(emp => {
            if (emp.id !== employeeId) return emp;
            return {
                ...emp,
                assignments: emp.assignments.filter(a => String(a._id) !== String(assignmentId)),
            };
        }));
    }, []);

    /* ---------- COLLISION DETECTION - chỉ xét droppables trong viewport của scroll container ---------- */
    const collisionDetection = useCallback((args) => {
        const scrollEl = topRightScrollRef.current;
        if (!scrollEl) return pointerWithin(args);

        const scrollRect = scrollEl.getBoundingClientRect();
        const filtered = args.droppableContainers.filter(container => {
            // UNASSIGNED_POOL không bị clip theo TopRight viewport
            if (container.data?.current?.dropType === 'UNASSIGNED_POOL') return true;

            const rect = container.rect.current;
            if (!rect) return true;
            return (
                rect.top    < scrollRect.bottom &&
                rect.bottom > scrollRect.top    &&
                rect.left   < scrollRect.right  &&
                rect.right  > scrollRect.left
            );
        });

        return pointerWithin({ ...args, droppableContainers: filtered });
    }, [topRightScrollRef]);

    /* ---------- BAR CLICK / DOUBLE-CLICK ---------- */
    const handleBarClick = useCallback((bar) => {
        const uid = bar.uniqueId || String(bar.id);
        setSelectedBarId(prev => prev === uid ? null : uid);
    }, []);

    const handleBarDoubleClick = useCallback((bar) => {
        setEditingBar(bar);
    }, []);

    const handleAssignmentUpdate = useCallback((assignmentId, updates) => {
        const idStr = String(assignmentId);
        const merge = (a) => idStr === String(a._id) ? { ...a, ...updates } : a;

        setEmployees(prev => prev.map(emp => ({
            ...emp,
            assignments: emp.assignments.map(merge),
        })));

        setUnassignments(prev => {
            const merged = prev.map(merge);
            const inPool = prev.some(a => idStr === String(a._id));

            if (inPool) {
                // Already in pool — remove if now fully staffed
                const target = merged.find(a => idStr === String(a._id));
                const assigneeCount = target?.assignees?.length ?? 0;
                const required = target?.requiredEmployees ?? 1;
                if (assigneeCount >= required) {
                    return merged.filter(a => idStr !== String(a._id));
                }
                return merged;
            }

            // Not in pool - find from employees and check if now understaffed
            let current = null;
            for (const emp of employees) {
                current = emp.assignments.find(a => idStr === String(a._id));
                if (current) break;
            }
            if (!current) return merged;

            const updated = { ...current, ...updates };
            const assigneeCount = updated.assignees?.length ?? 0;
            const required = updated.requiredEmployees ?? 1;
            if (assigneeCount < required) {
                return [...merged, updated];
            }
            return merged;
        });
    }, [employees]);

    /* ---------- UNASSIGN ---------- */
    const handleUnassign = useCallback(async (assignmentId, fromEmployeeId, rawAssignment) => {
        const remainingAssignees = (rawAssignment.assignees || [])
            .filter(ae => String(ae.employeeId) !== String(fromEmployeeId));
        const optimisticUnassigned = { ...rawAssignment, assignees: remainingAssignees, __optimistic: true };

        removeFromEmployee(fromEmployeeId, assignmentId);
        // Update assignees count in other employees who share this assignment
        setEmployees(prev => prev.map(emp => ({
            ...emp,
            assignments: emp.assignments.map(a =>
                String(a._id) === String(assignmentId) ? { ...a, assignees: remainingAssignees } : a
            ),
        })));
        setUnassignments(prev => {
            const idx = prev.findIndex(a => String(a._id) === assignmentId);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = optimisticUnassigned;
                return next;
            }
            return [optimisticUnassigned, ...prev];
        });

        try {
            const res = await api.post('/assignments/unassign', { assignmentId, employeeId: fromEmployeeId });
            const committed = { ...rawAssignment, assignees: res.data.assignees || [] };
            setEmployees(prev => prev.map(emp => ({
                ...emp,
                assignments: emp.assignments.map(a =>
                    String(a._id) === String(assignmentId) ? committed : a
                ),
            })));
            setUnassignments(prev => prev.map(a =>
                a.__optimistic && String(a._id) === assignmentId ? committed : a
            ));
            toast.success("Task unassigned.");
        } catch (err) {
            setUnassignments(prev => prev.filter(a =>
                !(a.__optimistic && String(a._id) === assignmentId)
            ));
            addToEmployee(fromEmployeeId, rawAssignment);
            toast.error(err?.response?.data?.message || "Unassignment failed!");
        }
    }, [removeFromEmployee, addToEmployee]);

    /* ---------- DRAG HANDLERS ---------- */

    const handleDragStart = useCallback((event) => {
        setActiveBar(event.active.data?.current?.bar || null);
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveBar(null);
    }, []);

    const handleDragEnd = useCallback(async (event) => {
        const { active, over } = event;
        setActiveBar(null);

        if (!over) return;

        const source = active.data.current;
        const target = over.data.current;
        if (!target) return;

        const draggingBar = source.bar;
        if (!draggingBar) return;

        const dragSource = draggingBar.dragSource;
        const rawAssignment = draggingBar.rawAssignment;
        const assignmentId = String(draggingBar.id);

        /* ── EMPLOYEE_ROW → UNASSIGNED_POOL: Unassign ── */
        if (dragSource === 'EMPLOYEE_ROW' && target.dropType === 'UNASSIGNED_POOL') {
            const fromEmployeeId = draggingBar.fromEmployeeId;
            if (!fromEmployeeId) return;
            await handleUnassign(assignmentId, fromEmployeeId, rawAssignment);
            return;
        }

        if (target.dropType !== 'EMPLOYEE_CELL') return;

        const targetEmployeeId = target.employeeId;
        if (!targetEmployeeId) return;

        const isCopy = event.activatorEvent.ctrlKey || event.activatorEvent.metaKey;

        /* ── EMPLOYEE_ROW → EMPLOYEE_CELL: Reassign ── */
        if (dragSource === 'EMPLOYEE_ROW') {
            const fromEmployeeId = draggingBar.fromEmployeeId;
            if (!fromEmployeeId || fromEmployeeId === targetEmployeeId) return;

            const alreadyAssigned = (rawAssignment.assignees || []).some(
                ae => String(ae.employeeId) === String(targetEmployeeId)
            );
            if (alreadyAssigned) {
                toast.error('Target employee already assigned to this shift!');
                return;
            }

            // Optimistic: move bar from source row to target row
            removeFromEmployee(fromEmployeeId, assignmentId);
            addToEmployee(targetEmployeeId, { ...rawAssignment, __optimistic: true });

            try {
                const res = await api.post(
                    '/assignments/reassign',
                    { assignmentId, fromEmployeeId, toEmployeeId: targetEmployeeId }
                );
                replaceInEmployee(targetEmployeeId, assignmentId, res.data);
                toast.success("Task re-assigned.");
            } catch (err) {
                // Rollback
                removeFromEmployee(targetEmployeeId, assignmentId);
                addToEmployee(fromEmployeeId, rawAssignment);
                const data = err?.response?.data;
                if (data?.requiresOverride) {
                    setLaborOverride({ type: 'reassign', assignmentId, fromEmployeeId, targetEmployeeId, rawAssignment, violations: data.violations });
                } else {
                    toast.error(data?.message || "Reassignment failed!");
                }
            }
            return;
        }

        /* ── UNASSIGNED_POOL → EMPLOYEE_CELL: Copy (Ctrl) ── */
        if (isCopy) {
            const tempId = `temp-copy-${Date.now()}`;
            const optimisticCopy = {
                ...rawAssignment,
                _id: tempId,
                assignees: [{ employeeId: targetEmployeeId, method: 'MANUAL' }],
                __optimistic: true,
            };
            addToEmployee(targetEmployeeId, optimisticCopy);

            try {
                const res = await api.post(
                    '/assignments/copy',
                    { assignmentId, employeeId: targetEmployeeId }
                );
                replaceInEmployee(targetEmployeeId, tempId, res.data);
                toast.success("Task copied.");
            } catch (err) {
                removeFromEmployee(targetEmployeeId, tempId);
                toast.error(err?.response?.data?.message || "Copy failed!");
            }
            return;
        }

        /* ── UNASSIGNED_POOL → EMPLOYEE_CELL: Assign ── */
        const alreadyAssigned = (rawAssignment.assignees || []).some(
            ae => String(ae.employeeId) === String(targetEmployeeId)
        );
        if (alreadyAssigned) {
            toast.error('Target employee already assigned to this shift!');
            return;
        }

        const requiredCount = rawAssignment.requiredEmployees ?? rawAssignment.shiftId?.requiredEmployees ?? 1;
        const currentCount  = (rawAssignment.assignees || []).length;

        if (currentCount >= requiredCount) {
            toast.error(`Shift is already full (${requiredCount}/${requiredCount}).`);
            return;
        }

        const newAssignees = [...(rawAssignment.assignees || []), { employeeId: targetEmployeeId, method: 'MANUAL' }];
        const willBeFull = newAssignees.length >= requiredCount;

        const optimisticAssignment = {
            ...rawAssignment,
            assignees: newAssignees,
            __optimistic: true,
        };

        if (willBeFull) {
            removeFromUnassigned(assignmentId);
        } else {
            setUnassignments(prev => prev.map(a =>
                String(a._id) === assignmentId ? { ...optimisticAssignment } : a
            ));
        }
        addToEmployee(targetEmployeeId, optimisticAssignment);
        // Update assignees count in other employees who share this assignment
        setEmployees(prev => prev.map(emp => {
            if (emp.id === targetEmployeeId) return emp;
            return {
                ...emp,
                assignments: emp.assignments.map(a =>
                    String(a._id) === String(assignmentId) ? { ...a, assignees: newAssignees } : a
                ),
            };
        }));

        try {
            const res = await api.post('/assignments/assign', { assignmentId, employeeId: targetEmployeeId });
            const committed = { ...rawAssignment, assignees: res.data.assignees || [] };
            // Update all employees with committed server data
            setEmployees(prev => prev.map(emp => ({
                ...emp,
                assignments: emp.assignments.map(a =>
                    String(a._id) === String(assignmentId) ? res.data : a
                ),
            })));
            if (!willBeFull) {
                setUnassignments(prev => prev.map(a =>
                    a.__optimistic && String(a._id) === assignmentId ? committed : a
                ));
            }
            toast.success("Task assigned.");
        } catch (err) {
            // Rollback optimistic update
            removeFromEmployee(targetEmployeeId, assignmentId);
            if (willBeFull) {
                setUnassignments(prev => [rawAssignment, ...prev]);
            } else {
                setUnassignments(prev => prev.map(a =>
                    String(a._id) === assignmentId ? rawAssignment : a
                ));
            }
            const data = err?.response?.data;
            if (data?.requiresOverride) {
                setLaborOverride({ type: 'assign', assignmentId, targetEmployeeId, rawAssignment, willBeFull, violations: data.violations });
            } else {
                toast.error(data?.message || "Assignment failed!");
            }
        }
    }, [
        addToEmployee,
        removeFromEmployee,
        replaceInEmployee,
        removeFromUnassigned,
        handleUnassign,
    ]);


    /* ---------- LABOR OVERRIDE CONFIRM ---------- */

    const handleOverrideConfirm = useCallback(async () => {
        if (!laborOverride) return;
        const { type, assignmentId, targetEmployeeId, fromEmployeeId, rawAssignment, willBeFull } = laborOverride;
        setLaborOverride(null);

        if (type === 'assign') {
            const newAssignees = [...(rawAssignment.assignees || []), { employeeId: targetEmployeeId, method: 'MANUAL' }];
            const optimistic   = { ...rawAssignment, assignees: newAssignees, __optimistic: true };
            if (willBeFull) removeFromUnassigned(assignmentId);
            else setUnassignments(prev => prev.map(a => String(a._id) === assignmentId ? optimistic : a));
            addToEmployee(targetEmployeeId, optimistic);
            setEmployees(prev => prev.map(emp => {
                if (emp.id === targetEmployeeId) return emp;
                return { ...emp, assignments: emp.assignments.map(a => String(a._id) === String(assignmentId) ? { ...a, assignees: newAssignees } : a) };
            }));
            try {
                const res = await api.post('/assignments/assign', { assignmentId, employeeId: targetEmployeeId, force: true });
                const committed = { ...rawAssignment, assignees: res.data.assignees || [] };
                setEmployees(prev => prev.map(emp => ({ ...emp, assignments: emp.assignments.map(a => String(a._id) === String(assignmentId) ? res.data : a) })));
                if (!willBeFull) setUnassignments(prev => prev.map(a => a.__optimistic && String(a._id) === assignmentId ? committed : a));
                toast.success("Task assigned (override).");
            } catch (err) {
                removeFromEmployee(targetEmployeeId, assignmentId);
                if (willBeFull) setUnassignments(prev => [rawAssignment, ...prev]);
                else setUnassignments(prev => prev.map(a => String(a._id) === assignmentId ? rawAssignment : a));
                toast.error(err?.response?.data?.message || "Override assignment failed!");
            }
        } else if (type === 'reassign') {
            removeFromEmployee(fromEmployeeId, assignmentId);
            addToEmployee(targetEmployeeId, { ...rawAssignment, __optimistic: true });
            try {
                const res = await api.post('/assignments/reassign', { assignmentId, fromEmployeeId, toEmployeeId: targetEmployeeId, force: true });
                replaceInEmployee(targetEmployeeId, assignmentId, res.data);
                toast.success("Task re-assigned (override).");
            } catch (err) {
                removeFromEmployee(targetEmployeeId, assignmentId);
                addToEmployee(fromEmployeeId, rawAssignment);
                toast.error(err?.response?.data?.message || "Override reassignment failed!");
            }
        }
    }, [
        laborOverride, 
        addToEmployee, 
        removeFromEmployee, 
        replaceInEmployee, 
        removeFromUnassigned,
    ]);

    const laborOverrideMessage = useMemo(() => {
        if (!laborOverride) return null;
        const errors   = laborOverride.violations.filter(v => v.severity === 'ERROR');
        const warnings = laborOverride.violations.filter(v => v.severity === 'WARNING');
        return (
            <Box>
                <Typography variant="body2" gutterBottom>
                    The following labor rule violations were detected:
                </Typography>
                {errors.map((v, i) => (
                    <Box key={`e${i}`} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                        <Typography variant="body2" color="error" fontWeight="bold" sx={{ minWidth: 56, flexShrink: 0 }}>
                            [ERROR]
                        </Typography>
                        <Typography variant="body2" color="error">
                            {v.name}: {v.message}
                        </Typography>
                    </Box>
                ))}
                {warnings.map((v, i) => (
                    <Box key={`w${i}`} sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                        <Typography variant="body2" color="warning.main" fontWeight="bold" sx={{ minWidth: 56, flexShrink: 0 }}>
                            [WARN]
                        </Typography>
                        <Typography variant="body2" color="warning.main">
                            {v.name}: {v.message}
                        </Typography>
                    </Box>
                ))}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    Do you want to override and proceed with the assignment anyway?
                </Typography>
            </Box>
        );
    }, [laborOverride]);

    /* ---------- CLIENT-SIDE EMPLOYEE FILTER + SORT ---------- */
    const displayEmployees = useMemo(() => {
        const {
            employeeCode = '', employeeName = '',
            ranks = [], homebases = [], departments = [], divisions = [],
            genders = [], workingModes = [],
            sorts = [{ field: 'employeeCode', dir: 'asc' }],
        } = employeeFilter || {};
        const codeQ = employeeCode.trim().toLowerCase();
        const nameQ = employeeName.trim().toLowerCase();
        const hasAny = codeQ || nameQ || ranks.length || homebases.length ||
                       departments.length || divisions.length || genders.length || workingModes.length;

        let result = hasAny
            ? employees.filter(({ employee: e }) => {
                if (codeQ              && !e.employeeCode?.toLowerCase().includes(codeQ)) return false;
                if (nameQ              && !e.employeeName?.toLowerCase().includes(nameQ)) return false;
                if (ranks.length         && !ranks.includes(e.rank))              return false;
                if (homebases.length     && !homebases.includes(e.homeBase))       return false;
                if (departments.length   && !departments.includes(e.department))   return false;
                if (divisions.length     && !divisions.includes(e.division))       return false;
                if (genders.length       && !genders.includes(e.gender))           return false;
                if (workingModes.length  && !workingModes.includes(e.workingMode)) return false;
                return true;
            })
            : employees;

        if (sorts.length) {
            result = [...result].sort((a, b) => {
                for (const { field, dir } of sorts) {
                    const getVal = (emp) => {
                        if (field === 'employeeCode') return (emp.employeeCode ?? '').toLowerCase();
                        if (field === 'isActive') return emp.employee?.isActive ? '1' : '0';
                        return String(emp.employee?.[field] ?? '').toLowerCase();
                    };
                    const cmp = dir === 'desc'
                        ? getVal(b).localeCompare(getVal(a))
                        : getVal(a).localeCompare(getVal(b));
                    if (cmp !== 0) return cmp;
                }
                return 0;
            });
        }

        return result;
    }, [employees, employeeFilter]);

    useEffect(() => {
        if (!exportRef) return;
        exportRef.current = () => exportScheduleToExcel({ employees: displayEmployees, filteredEmployeeIds, startDate, endDate });
    }, [exportRef, displayEmployees, filteredEmployeeIds, startDate, endDate]);

    return (
        <Box
            sx={{
                height: "100%",
                borderRadius: 1,
                border: 1,
                borderColor: 'neutral.deep',
                position: 'relative',
            }}
        >
            {/* Re-fetch indicator */}
            {isFetching && (
                <Box sx={{
                    zIndex: 10,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.9,
                    borderRadius: 1,
                    p: 2,
                    gap: 2,
                }}>
                    <CircularProgress size={36} />
                    <Typography variant="body1">
                        Loading...
                    </Typography>
                </Box>
            )}

            {loading ? (
                <FlexCenter height="100%">
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height="100%">
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </FlexCenter>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={collisionDetection}
                    autoScroll={{ threshold: 50, acceleration: 10 }}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <Portal>
                        <DragOverlay
                            dropAnimation={null}
                            style={{ zIndex: 9999 }}
                        >
                            {activeBar && (
                                <Box sx={{
                                    pointerEvents: 'none',
                                    position: 'relative',
                                    width: activeBar.duration * cellWidth,
                                    height: rowHeight,
                                }}>
                                    <GanttBar
                                        bar={{ ...activeBar, startSlot: 0, laneIndex: 0 }}
                                        cellWidth={cellWidth}
                                        rowHeight={rowHeight}
                                        isOverlay
                                        sx={{
                                            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                                            opacity: 0.95,
                                        }}
                                    />
                                </Box>
                            )}
                        </DragOverlay>
                    </Portal>
                    <GanttLayout
                        topDefaultSize={70}
                        bottomCollapsible={false}
                        topLeft={{
                            defaultSize: leftSize,
                            minSize: 0,
                            content: (
                                <TopLeftPanel
                                    syncY={syncY}
                                    topLeftScrollRef={topLeftScrollRef}
                                    topRightScrollRef={topRightScrollRef}
                                    bottomLeftScrollRef={bottomLeftScrollRef}
                                    rowHeight={rowHeight}
                                    headerName={`Staff (${displayEmployees?.length || 0})`}
                                    fontSize={"0.7rem"}
                                    data={displayEmployees}
                                    onRowClick={(row) => setScheduleEmployee(row)}
                                />
                            ),
                        }}
                        topRight={{
                            defaultSize: rightSize,
                            minSize: 0,
                            content: (
                                <TopRightPanel
                                    syncX={syncX}
                                    syncY={syncY}
                                    timelineScrollRef={timelineScrollRef}
                                    topLeftScrollRef={topLeftScrollRef}
                                    topRightScrollRef={topRightScrollRef}
                                    bottomLeftScrollRef={bottomLeftScrollRef}
                                    bottomRightScrollRef={bottomRightScrollRef}
                                    rowHeight={rowHeight}
                                    cellWidth={cellWidth}
                                    startDate={startDate}
                                    endDate={endDate}
                                    employees={displayEmployees}
                                    selectedBarId={selectedBarId}
                                    onBarClick={handleBarClick}
                                    onBarDoubleClick={handleBarDoubleClick}
                                    canDelete={canDelete}
                                    onUnassign={(assignmentId, employeeId, rawAssignment) => {
                                        handleUnassign(assignmentId, employeeId, rawAssignment);
                                        setSelectedBarId(null);
                                    }}
                                />
                            ),
                        }}
                        bottomLeft={{
                            defaultSize: leftSize,
                            minSize: 0,
                            content: (
                                <BottomLeftPanel
                                    bottomLeftScrollRef={bottomLeftScrollRef}
                                    startDate={startDate}
                                    endDate={endDate}
                                    onOptimisticCreate={addUnassignmentsOptimistic}
                                    onOptimisticCommit={replaceOptimisticAssignments}
                                    onOptimisticRollback={rollbackOptimisticAssignments}
                                    canCreate={canCreate}
                                    canDelete={canDelete}
                                    onDeleted={loadData}
                                />
                            ),
                        }}
                        bottomRight={{
                            defaultSize: rightSize,
                            minSize: 0,
                            content: (
                                <BottomRightPanel
                                    syncX={syncX}
                                    timelineScrollRef={timelineScrollRef}
                                    topRightScrollRef={topRightScrollRef}
                                    bottomRightScrollRef={bottomRightScrollRef}
                                    rowHeight={rowHeight}
                                    cellWidth={cellWidth}
                                    startDate={startDate}
                                    endDate={endDate}
                                    unassignments={unassignments}
                                    selectedBarId={selectedBarId}
                                    onBarClick={handleBarClick}
                                    onBarDoubleClick={handleBarDoubleClick}
                                    canDelete={canDelete}
                                    onDeleted={() => {
                                        loadData();
                                        setSelectedBarId(null);
                                    }}
                                />
                            ),
                        }}
                    />
                </DndContext>
            )}
            <ConfirmDialog
                open={!!laborOverride}
                onClose={() => setLaborOverride(null)}
                onConfirm={handleOverrideConfirm}
                title="Labor Rule Violation - Override?"
                message={laborOverrideMessage}
                confirmText="Proceed"
                cancelText="Cancel"
                confirmColor="warning"
            />
            <AssignmentDialog
                bar={editingBar}
                onClose={() => setEditingBar(null)}
                onSaved={handleAssignmentUpdate}
                onUnassign={handleUnassign}
                canEdit={canEdit}
                employeeMap={employeeMap}
            />
            <AutoAssignmentDialog
                open={autoAssignmentOpen}
                onClose={onAutoAssignmentClose}
                startDate={startDate}
                endDate={endDate}
                onApplied={loadData}
                employeeIds={filteredEmployeeIds}
            />
            <EmployeeScheduleDialog
                employee={scheduleEmployee}
                startDate={startDate}
                endDate={endDate}
                onClose={() => setScheduleEmployee(null)}
                onPublished={loadData}
                canPublish={canPublish}
            />
        </Box>
    );
};


export default React.memo(GanttRoot);
